import qs, { ParsedQs } from 'qs'
import { defaultTo, isArray, isEmpty, isNil, omitBy, toNumber } from 'lodash-es'
import { AgGridRowId } from '@/services/common/types'

export class Utils {
  static isProd() {
    return window.location.hostname === 'moms.musinsalogistics.co.kr'
  }

  static isLocal() {
    return window.location.hostname.startsWith('localhost')
  }

  static uuid() {
    return '_' + Math.random().toString(36).substring(2, 11)
  }

  static assetUrl(assetPath: string) {
    const publicUrl = process.env.PUBLIC_URL || ''
    const baseUrl = publicUrl.endsWith('/') ? publicUrl.slice(0, -1) : publicUrl
    const pathname = assetPath[0] === '/' ? assetPath.slice(1) : assetPath

    return `${baseUrl}/${pathname}`
  }

  static getApiErrMessage(e: unknown, defaultMessage: string) {
    // @ts-ignore
    const message = e?.response?.data?.meta?.message || e.response?.data?.detail || e.response?.data?.message
    return (Array.isArray(message) ? message.join('\n') : message || defaultMessage) + '<span style="color:red"> *</span>'
  }

  // LOGISPRD-9057 — axios 에러에서 BE meta.errorCode 추출 (Okta 멤버십 403 분기 등). string 이 아니면 undefined.
  static getApiErrorCode(e: unknown): string | undefined {
    const code = (e as { response?: { data?: { meta?: { errorCode?: unknown } } } })?.response?.data?.meta?.errorCode
    return typeof code === 'string' ? code : undefined
  }

  // LOGISPRD-15703 — axios 에러의 HTTP 상태. number 가 아니면 undefined.
  static getApiStatus(e: unknown): number | undefined {
    const status = (e as { response?: { status?: unknown } })?.response?.status
    return typeof status === 'number' ? status : undefined
  }

  static checkContinuousText(text: string, count: number) {
    if (!text) return false

    let continuousCount = 0
    text.split('').some((_, idx) => {
      // break
      if (continuousCount >= count - 1) return true

      // continue
      if (idx > 0 && text[idx] !== text[idx - 1]) {
        continuousCount = 1
      } else if (idx > 0) {
        continuousCount += 1
      }

      return false
    })

    return continuousCount >= count
  }

  static qsParse = <T = ParsedQs>(search: string) => {
    return (search ? qs.parse(search.slice(1), { comma: true, parseArrays: true }) : {}) as T
  }

  static qsStringify = <T = Record<string, never>>(params?: T) => {
    const omittedParams = omitBy(params || {}, (val) => {
      if (isNil(val)) return true
      return isArray(val) && (val as string[]).every((v) => !v) // array인 경우 모두 빈값이면 안됨.
    })

    return isEmpty(omittedParams) ? '' : `?${qs.stringify(omittedParams, { arrayFormat: 'comma', encode: false })}`
  }

  static getCommaNum = (v?: string | number) =>
    !isNil(v)
      ? defaultTo(toNumber(v), 0)
          .toString()
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      : ''

  static chunkArray<T>(array: T[], size: number): T[][] {
    const result: T[][] = []
    for (let i = 0; i < array.length; i += size) {
      result.push(array.slice(i, i + size))
    }
    return result
  }

  static createSelectOptions<T, K extends keyof T>(data: T[], labelKey?: K, valueKey?: K): SelectOption<T[K]>[] | SelectOption<string>[] {
    if (data.length === 0) {
      return []
    }

    if (typeof data[0] === 'string') {
      return (data as string[]).map((item) => ({
        label: item,
        value: item,
      }))
    }

    return (data as T[]).map((item) => ({
      label: String(item[labelKey!]),
      value: item[valueKey!],
    }))
  }

  // items 배열의 각 항목에 id 필드 추가 (row id)
  static reformatArrayToAddIds = <T>(items: T[], prefix = ''): (T & AgGridRowId)[] => {
    if (!items || !Array.isArray(items)) return []

    return items.map((item, index) => ({
      ...item,
      id: prefix + (index + 1),
    }))
  }

  // 빈 문자열을 null로 변환하는 유틸리티 함수
  static emptyStringToNull = (value: string | null | undefined): string | null => {
    return value === '' ? null : value || null
  }

  static emptyArrayToNull = (value: string[] | null | undefined): string[] | null => {
    return value?.length === 0 ? null : value || null
  }

  static normalizeToArray = (value: string | string[] | null | undefined): string[] => {
    if (!value) return []
    if (Array.isArray(value)) return value
    return [value]
  }
}
